import { useSchedules } from 'features/Responder/features/Schedules/contexts/SchedulesContext';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Container, Row } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { RRule } from 'rrule';
import {
  enrichSchedule,
  formatRruleText,
  generateEventsBetween,
  getFixedDtstart,
  getScheduleEventProps,
  mergeAssignmentRRules,
} from 'utils/recurrenceUtils';
import ScheduleCalendar from '../../../ScheduleBuilder/components/RruleBuilder/components/ScheduleCalendar';
import { useAssignments } from '../../contexts/AssignmentsContext';
import { DailyOccurrence } from './components/DailyOccurrence';
import { MonthlyOccurrence } from './components/MonthlyOccurrence';
import { OccurrenceCard } from './components/OccurrenceCard';
import { WeeklyOccurrence } from './components/WeeklyOccurrence';
import { YearlyOccurrence } from './components/YearlyOccurrence';

export default function AssignmentBuilder() {
  // Track the currently selected occurrence for editing in the calendar view.
  const [activeEditOccurrence, setActiveEditOccurrence] = useState(null);

  const {
    unsavedChanges,
    setUnsavedChanges,
    clearActiveEditAssignments,
    uploadAssignments,
    activeEditAssignments,
  } = useAssignments();
  const { schedules, activeEditSchedule, setActiveEditSchedule } = useSchedules();

  // Get the scheduleId from the URL query parameters to determine which schedule's assignments to display/edit.
  const [searchParams] = useSearchParams();

  // When the scheduleId in the URL changes, update the activeEditSchedule in context to match.
  useEffect(() => {
    const id = searchParams.get('scheduleId');
    let sch = schedules.find((sch) => `${sch.id}` === `${id}`) || null;
    setActiveEditSchedule(sch);
    setActiveEditOccurrence(null);
  }, [searchParams, schedules]);

  // Determine the type of pattern (daily, weekly, monthly, yearly) based on the RRule options. This
  const determineRenderType = (schedule) => {
    const options = RRule.parseString(schedule.recurrence);
    if (options.freq === RRule.DAILY) {
      return 'daily';
    } else if (options.freq === RRule.WEEKLY) {
      return 'weekly';
    } else if (options.freq === RRule.MONTHLY) {
      return 'monthly';
    } else if (options.freq === RRule.YEARLY) {
      return 'yearly';
    }
    return 'other';
  };

  // Generate example occurrences for one interval of the RRule pattern.
  // This is used to show a sample of upcoming occurrences in the pattern view
  const generateExampleOccurrences = useCallback((schedule) => {
    if (!schedule || !schedule.recurrence) return [];

    const options = RRule.parseString(schedule.recurrence);

    // Use the DTSTART from the RRule as the starting point for generating occurrences
    let exampleStart = getFixedDtstart(options);
    let exampleEnd;

    const interval = options.interval || 1;
    switch (options.freq) {
      // For daily and weekly patterns, calculate the end date to show one full interval of occurrences.
      case RRule.DAILY:
        exampleEnd = new Date(exampleStart.getTime() + interval * 24 * 60 * 60 * 1000 - 1000);
        break;
      // For weekly patterns, show one full week of occurrences based on the interval.
      case RRule.WEEKLY:
        exampleEnd = new Date(exampleStart.getTime() + interval * 7 * 24 * 60 * 60 * 1000 - 1000);
        break;
      // For monthly patterns, we need to account for varying month lengths and ensure we capture the next occurrence,
      // even if it falls later in the month than the DTSTART day.
      case RRule.MONTHLY: {
        // Start from the 1st of the DTSTART month, but advance to the current month
        // if today is later — this ensures we always capture upcoming occurrences even
        // when the recurring weekday in the first month falls before DTSTART.
        const dtMonthStart = new Date(
          exampleStart.getFullYear(),
          exampleStart.getMonth(),
          1,
          exampleStart.getHours(),
          exampleStart.getMinutes(),
          0,
          0,
        );
        const now = new Date();
        const nowMonthStart = new Date(
          now.getFullYear(),
          now.getMonth(),
          1,
          exampleStart.getHours(),
          exampleStart.getMinutes(),
          0,
          0,
        );
        exampleStart = nowMonthStart > dtMonthStart ? nowMonthStart : dtMonthStart;
        exampleEnd = new Date(exampleStart);
        // For intervals > 1 show a full year so all occurrences within the year are visible.
        // For interval = 1 span two months (enough to always catch the next occurrence).
        const monthSpan = interval > 1 ? 12 : interval * 2;
        exampleEnd.setMonth(exampleEnd.getMonth() + monthSpan);
        exampleEnd = new Date(exampleEnd.getTime() - 1000); // Subtract 1 second
        break;
      }
      // For yearly patterns, show one full year of occurrences based on the interval.
      case RRule.YEARLY:
        exampleEnd = new Date(exampleStart);
        exampleEnd.setFullYear(exampleEnd.getFullYear() + interval);
        exampleEnd = new Date(exampleEnd.getTime() - 1000); // Subtract 1 second
        break;
      default:
        // Fallback: 30 days
        exampleEnd = new Date(exampleStart.getTime() + 30 * 24 * 60 * 60 * 1000 - 1000);
        break;
    }

    let occurrences = generateEventsBetween(schedule, exampleStart, exampleEnd);

    return occurrences;
  }, []);

  // State for pattern view
  const eventList = useMemo(() => {
    if (!activeEditSchedule) return [];
    return generateExampleOccurrences(activeEditSchedule);
  }, [activeEditSchedule, generateExampleOccurrences]);

  // State for calendar view
  const calendarEvents = useMemo(() => {
    if (!activeEditSchedule) return [];
    let options = RRule.parseString(activeEditSchedule.recurrence);
    let exampleStart = getFixedDtstart(options);

    // Get the rest of the current year's events
    return generateEventsBetween(
      activeEditSchedule,
      exampleStart,
      new Date(new Date().getFullYear(), 11, 31), // Dec 31 of this year
    );
  }, [activeEditSchedule]);

  // Determine the render type (daily, weekly, monthly, yearly) based on the active schedule's RRule.
  const renderType = useMemo(() => {
    if (!activeEditSchedule) return null;
    return determineRenderType(activeEditSchedule);
  }, [activeEditSchedule]);

  // UI state
  const [activeTab, setActiveTab] = useState('pattern');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const successTimerRef = useRef(null);

  // Monthly pattern drill-down: when set, show a focused day view instead of the full month grid.
  const [focusedDay, setFocusedDay] = useState(null); // { events, label }

  // Reset focused day when the schedule or tab changes.
  useEffect(() => {
    setFocusedDay(null);
  }, [activeEditSchedule, activeTab]);

  return (
    <div className="p-3 rounded-2 h-100 d-flex flex-column" style={{ background: '#0b1826' }}>
      <Container fluid className="d-flex flex-column mb-3" style={{ flex: '0 0 auto' }}>
        <Row>
          <h2 className="mb-4 fw-semibold">Assignments</h2>
        </Row>
        <Row>
          {/* Example: Display the scheduleId for debug */}
          <h4 className="text-white">{activeEditSchedule?.name}</h4>
        </Row>
        {activeEditSchedule && formatRruleText(activeEditSchedule.recurrence)}
      </Container>

      {activeEditSchedule && (
        <Container>
          <Row className="mb-2 justify-content-start align-items-center flex-nowrap">
            <div className="d-flex flex-row">
              <Button
                variant={activeTab === 'pattern' ? 'primary' : 'secondary'}
                className="me-2"
                onClick={() => setActiveTab('pattern')}
              >
                Pattern View
              </Button>
              <Button
                variant={activeTab === 'calendar' ? 'primary' : 'secondary'}
                onClick={() => setActiveTab('calendar')}
              >
                Calendar View
              </Button>
            </div>
          </Row>
        </Container>
      )}

      <div className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
        <Container fluid className="d-flex flex-column justify-content-between mb-3">
          {/* Example: Render events for the active schedule */}
          {activeEditSchedule && activeTab === 'pattern' && (
            <div className="text-white">
              <h4>
                {renderType
                  ? renderType.charAt(0).toUpperCase() + renderType.slice(1)
                  : 'Unknown'}{' '}
              </h4>
              {renderType === 'daily' && (
                <DailyOccurrence events={eventList} source="pattern-daily" showAssigner />
              )}
              {renderType === 'weekly' && (
                <WeeklyOccurrence events={eventList} source="pattern-weekly" showAssigner />
              )}
              {renderType === 'monthly' && !focusedDay && (
                <MonthlyOccurrence
                  events={eventList}
                  source="pattern-monthly"
                  showAssigner
                  onDrillDown={(events, label) => setFocusedDay({ events, label })}
                />
              )}
              {renderType === 'monthly' && focusedDay && (
                <div>
                  <Button
                    variant="outline-light"
                    size="sm"
                    className="mb-3"
                    onClick={() => setFocusedDay(null)}
                  >
                    ← Back to Month
                  </Button>
                  <h5 className="text-white mb-2">{focusedDay.label}</h5>
                  <DailyOccurrence
                    events={focusedDay.events}
                    source="pattern-monthly-day"
                    showAssigner
                  />
                </div>
              )}
              {renderType === 'yearly' && (
                <YearlyOccurrence events={eventList} source="pattern-yearly" showAssigner />
              )}
              {renderType !== 'daily' &&
                renderType !== 'weekly' &&
                renderType !== 'monthly' &&
                renderType !== 'yearly' && <div>RRule invalid.</div>}
            </div>
          )}
          {activeEditSchedule && activeTab === 'calendar' && (
            <ScheduleCalendar
              height={500}
              collapseDuplicateDays
              showAssigner
              onSelectEvent={(event) => {
                if (unsavedChanges) {
                  alert('You have unsaved changes. Please save before editing another occurrence.');
                  return;
                }
                setActiveEditOccurrence(event);
              }}
              events={calendarEvents}
              showDaily
              selectedEvent={activeEditOccurrence}
              eventPropGetter={(event) => {
                let props = getScheduleEventProps(event, enrichSchedule(activeEditSchedule));
                return props;
              }}
            />
          )}
          {
            // selected occurrence, display single card below with activeEditOccurrence
            activeEditOccurrence && activeTab === 'calendar' && (
              <div className="mt-3">
                <OccurrenceCard event={activeEditOccurrence} source="calendar" showAssigner />
              </div>
            )
          }
        </Container>
      </div>
      {activeEditSchedule && (
        <div className="d-flex justify-content-end mt-auto pt-4 flex-shrink-0">
          {uploadSuccess && (
            <span className="align-self-center me-3 fw-semibold" style={{ color: '#4caf50' }}>
              Assignments saved successfully!
            </span>
          )}
          {unsavedChanges && (
            <Button
              variant="secondary"
              className="me-3 fw-semibold px-4 py-2"
              onClick={() => {
                clearActiveEditAssignments();
                setUnsavedChanges(false);
              }}
            >
              Discard
            </Button>
          )}
          <Button
            variant="primary"
            className="fw-semibold px-4 py-2 border-0"
            onClick={async () => {
              await uploadAssignments(mergeAssignmentRRules(activeEditAssignments));
              setUploadSuccess(true);
              clearTimeout(successTimerRef.current);
              successTimerRef.current = setTimeout(() => setUploadSuccess(false), 5000);
            }}
          >
            Upload
          </Button>
        </div>
      )}
      {!activeEditSchedule && (
        <div className="d-flex justify-content-center align-items-center h-100 text-white">
          <h4>Select a schedule to view details</h4>
        </div>
      )}
    </div>
  );
}
